import { useQuery } from "@apollo/client/react";
import { Button, Grid, IconButton, Paper, Tooltip, styled } from "@mui/material";
import { GridColDef, getGridStringOperators } from "@mui/x-data-grid";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { UsersIcon } from "@phosphor-icons/react/dist/csr/Users";
import { ReactNode, SyntheticEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IfPermission } from "../../../components";
import { AnchorHoverLink } from "../../../components/ui/common";
import Permissions from "../../../constants/permissions";
import { AccountV2, AccountsV2Document } from "../../../graphql/types";
import type { AccountsV2Query, AccountsV2QueryVariables } from "../../../graphql/types";
import { CreateAccount } from "../components/CreateAccount";

const Container = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  height: "60vh",
  width: "100%",
}));

const GrayGroupIcon = styled(UsersIcon)(({ theme }) => ({
  color: theme.palette.text40.main,
}));

const AccountLink = ({ url_name, children }: { url_name: string; children?: ReactNode }) => {
  const url = `/app/${url_name}/dashboard`;
  const navigate = useNavigate();
  const onClick = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(url);
  };

  return <AnchorHoverLink onClick={onClick}>{children}</AnchorHoverLink>;
};
const nameUrlNameOperators = getGridStringOperators().filter(({ value }) =>
  ["equals", "contains", "startsWith"].includes(value)
);

const AccountsTable = ({ accounts, loading }: { accounts: AccountV2[]; loading: boolean }) => {
  const navigate = useNavigate();
  const accountsColumns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      filterOperators: nameUrlNameOperators,
    },
    {
      field: "urlName",
      headerName: "URL Name",
      flex: 1,
      filterOperators: nameUrlNameOperators,
      renderCell: (params) => {
        return <AccountLink url_name={params.row.urlName}>{params.row.urlName}</AccountLink>;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <IfPermission permission={Permissions.ManageUsers}>
            <Tooltip id="tooltip" title={`View details and users`} placement="bottom" arrow>
              <IconButton
                onClick={() => navigate(`/${params.row.urlName}/users`)}
                data-testid={`go-to-account-details-${params.row.urlName}`}
              >
                <GrayGroupIcon />
              </IconButton>
            </Tooltip>
          </IfPermission>
        );
      },
    },
  ];
  const rows: {
    id: string;
    urlName: string;
    name: string;
  }[] = [];
  if (!loading) {
    accounts.forEach((account: AccountV2) => {
      rows.push({
        id: account.urlName,
        urlName: account.urlName,
        name: account.name,
      });
    });
  }

  return (
    <Container variant="outlined" data-testid="accounts-table">
      <DataGridPro
        rows={rows}
        columns={accountsColumns}
        disableRowSelectionOnClick
        hideFooterPagination
        disableColumnPinning
        showToolbar
        disableDensitySelector
        isCellEditable={() => false}
        loading={loading}
      />
    </Container>
  );
};

const Accounts = () => {
  const { data, loading } = useQuery<AccountsV2Query, AccountsV2QueryVariables>(AccountsV2Document, {
    fetchPolicy: "cache-and-network",
  });

  const accounts = data?.accountsV2?.edges?.map((edge) => edge.node) || [];

  return <AccountsTable accounts={accounts} loading={loading} />;
};

const AccountsPage = () => {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <CreateAccount showModal={showModal} setShowModal={setShowModal} />
      <Grid container>
        <IfPermission permission={Permissions.ManageAccounts}>
          <Grid sx={{ justifySelf: "flex-end", marginLeft: "auto" }}>
            <Button onClick={() => setShowModal(true)} startIcon={<PlusIcon />} data-testid="add-account-button">
              Account
            </Button>
          </Grid>
        </IfPermission>
        <Grid size={12} sx={{ pt: 4 }}>
          <Accounts />
        </Grid>
      </Grid>
    </>
  );
};

export { AccountsPage };
